import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useFormContext } from 'react-hook-form';
import { Button, Flex, Input, Text, useDisclosure } from '@chakra-ui/react';
import { ReCAPTCHA } from 'react-google-recaptcha';
import { RECAPTCHA_TOKEN_FIELD } from '../../../../../constants/recaptchable';
import authenticationService from '../../../../../service/authenticationService';
import pnotifyService from '../../../../../service/pnotifyService';
import { QUOTA_LIMIT_ERROR_TYPE } from '../../../../../shop/constants/quota_limit';
import shoplineCaptcha from '../../../../../utils/shoplineCaptcha';
import extractServerErrorMessages from '../../../../SignupLogin/utils/extractServerErrorMessages';
import { SECTION_STATUS } from '../../../constants';
import { useAppContext } from '../../../context/appContext';
import { FIELD } from '../constants/field';
import { MESSAGE_TYPE } from '../constants/message';
import { useMessageContext } from '../context/messageContext';
import { useProfileContext } from '../context/profileContext';
import EmailVerificationModal from './EmailVerificationModal';
import FormField from './FormField';
import FormIconHint from './FormIconHint';

const UPDATE_EMAIL_BUTTON_ID = 'member-center-profile-verify-email_button';

const EmailField = () => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { t } = useTranslation();
  const { register, errors, getValues, trigger, setError, formState } =
    useFormContext();
  const { isSubmitted } = formState;
  const hasError = Boolean(errors.email);
  const { userData, submitError } = useProfileContext();
  const { setMessages } = useMessageContext();
  const {
    currentUser,
    isRecaptchaEnabled,
    grecaptcha,
    recaptchaSiteKey,
    userSetting,
    slFeatureService,
  } = useAppContext();
  const [status, setStatus] = useState(SECTION_STATUS.INITIAL);
  const [reachedRateLimit, setReachedRateLimit] = useState(false);
  const [shoplineCaptchaListener, setShoplineCaptchaListener] = useState(null);
  const reCAPTCHARef = useRef();
  const isEmailSubmitted = useRef(false);
  const {
    [FIELD.IS_EMAIL_CONFIRMED]: isEmailConfirm,
    [FIELD.UNCONFIRMED_EMAIL]: unconfirmedEmail,
  } = userData;
  const shouldVerifyEmail = userSetting.email_verification?.status;
  const shouldVerifyLogin = userSetting.login_with_verification?.status;
  const shouldShowVerifyButton =
    shouldVerifyEmail && !isEmailConfirm && !unconfirmedEmail;
  const hasShoplineCaptcha = slFeatureService.hasFeature('shopline_captcha');
  const hasShoplineEmailCaptcha =
    hasShoplineCaptcha && slFeatureService.hasFeature('shopline_email_captcha');
  const enabledRecaptcha = isRecaptchaEnabled || hasShoplineEmailCaptcha;

  useEffect(() => {
    if (isSubmitted && !isEmailSubmitted.current) {
      isEmailSubmitted.current = true;
    }
  }, [isSubmitted]);

  useEffect(() => {
    if (submitError?.[FIELD.EMAIL]) {
      setError(FIELD.EMAIL, { message: submitError[FIELD.EMAIL] });
    }
  }, [setError, submitError]);

  const handleEmailChange = () => {
    if (isEmailSubmitted.current || hasError) {
      trigger(FIELD.EMAIL);
    }
  };

  const sendVerificationEmail = async () => {
    try {
      await authenticationService.sendVerificationEmail();
      onOpen();
      setStatus(SECTION_STATUS.SUCCESS);
    } catch (error) {
      setStatus(SECTION_STATUS.FAILURE);
      const {
        error_code,
        error: errorMessage = '',
        reached_rate_limit = false,
      } = error?.response?.data || {};
      if (error_code === QUOTA_LIMIT_ERROR_TYPE.TRIAL_EMAIL_QUOTA_EXCEEDED) {
        pnotifyService.notify(
          t('verification.email.trial_merchant.reach_limit.failure'),
          {
            customClass: 'error',
            icon: 'fa fa-exclamation-triangle',
          },
        );
        return;
      }
      setMessages([{ message: errorMessage, type: MESSAGE_TYPE.ERROR }]);
      setReachedRateLimit(reached_rate_limit);
      console.error(errorMessage);
    }
  };
  const updateEmail = async ({ shoplineCaptchaMToken }) => {
    try {
      isEmailSubmitted.current = true;
      const isValid = await trigger(FIELD.EMAIL);
      if (!isValid) {
        return;
      }
      setStatus(SECTION_STATUS.LOADING);
      const payload = {
        user: { email: getValues(FIELD.EMAIL) },
        skip_email_verification: true,
        ...(hasShoplineEmailCaptcha
          ? {
              m_token: shoplineCaptchaMToken,
              dfp_token: await shoplineCaptcha.getTokenAsync(),
            }
          : {}),
      };
      if (enabledRecaptcha) {
        payload[RECAPTCHA_TOKEN_FIELD] =
          await reCAPTCHARef.current?.executeAsync?.();
        reCAPTCHARef.current?.reset?.();
      }
      const response = await authenticationService.updateUser(
        currentUser._id,
        payload,
      );
      if (!response.data.result) {
        throw response;
      }
      sendVerificationEmail();
    } catch (error) {
      setStatus(SECTION_STATUS.FAILURE);
      if (error?.data?.data?.email?.[0] === 'is already taken') {
        setError(FIELD.EMAIL, { message: 'form.validation.unique' });
      } else if (error?.response?.data?.error?.[0]) {
        setMessages([
          { message: error.response.data.error[0], type: MESSAGE_TYPE.ERROR },
        ]);
      } else {
        setError(FIELD.EMAIL, {
          messageStr: extractServerErrorMessages(error?.response?.data ?? {}),
        });
      }
    }
  };

  useEffect(() => {
    if (shouldShowVerifyButton && hasShoplineEmailCaptcha) {
      const listener = shoplineCaptcha.getListener(
        UPDATE_EMAIL_BUTTON_ID,
        (shoplineCaptchaMToken) => updateEmail({ shoplineCaptchaMToken }),
      );
      setShoplineCaptchaListener(listener);

      return () => listener?.destroy();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <FormField label={t('membership.profile.email')} hasError={hasError}>
        <Input
          name={FIELD.EMAIL}
          ref={register({ setValueAs: (value) => value || null })}
          onChange={handleEmailChange}
          data-e2e-id="member-center-profile-email_field"
        />
        {shouldVerifyEmail && (
          <Flex justifyContent="flex-end" gap="4px" alignItems="center">
            {isEmailConfirm ? (
              <FormIconHint
                isVerified
                icon={
                  <i className="fa fa-icon fa-check" aria-hidden="true"></i>
                }
              >
                {t('membership.profile.verified_success')}
              </FormIconHint>
            ) : (
              <FormIconHint
                icon={
                  <i
                    className="fa fa-icon fa-exclamation-triangle"
                    aria-hidden="true"
                  />
                }
              >
                {t('membership.profile.not_verified_yet')}
              </FormIconHint>
            )}
          </Flex>
        )}
        {hasError && (
          <Text variant="errorMessage" textAlign="right" mt="0.5">
            {errors.email.messageStr ??
              t(errors.email.message, {
                field_name: t('membership.profile.email'),
              })}
          </Text>
        )}
        {shouldVerifyLogin && unconfirmedEmail && (
          <Text
            variant="hint"
            className="member-center-profile__hint-text member-center-profile__email-hint"
            textAlign="left"
          >
            {t('membership.profile.verification_hint', { unconfirmedEmail })}
          </Text>
        )}
        {shouldShowVerifyButton && (
          <>
            <Button
              variant="fillSuccess"
              type="button"
              onClick={!shoplineCaptchaListener && updateEmail}
              disabled={reachedRateLimit || status === SECTION_STATUS.LOADING}
              mt={2}
              isLoading={status === SECTION_STATUS.LOADING}
              data-e2e-id="member-center-profile-verify-email_button"
              className="member-center-profile__primary-button"
              id={UPDATE_EMAIL_BUTTON_ID}
            >
              {t('membership.profile.send_verification_email')}
            </Button>
            {enabledRecaptcha && (
              <ReCAPTCHA
                ref={reCAPTCHARef}
                size="invisible"
                grecaptcha={grecaptcha}
                sitekey={recaptchaSiteKey}
              />
            )}
          </>
        )}
      </FormField>
      <EmailVerificationModal
        isOpen={isOpen}
        onClose={onClose}
        email={getValues(FIELD.EMAIL)}
      />
    </>
  );
};

export default EmailField;
